/**
 * ATSScoreCard.jsx
 * ─────────────────────────────────────────────────────────────
 * Analyses resume text against an optional job description and
 * renders a rich ATS score breakdown.
 *
 * Props:
 *   resumeText   : string  — plain-text dump of the resume
 *   onSkip       : () => void
 *   onContinue   : (scoreData) => void  — called after analysis
 * ─────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, AlertCircle, Info, ChevronRight, Loader, Zap, SkipForward } from 'lucide-react';

/* ── Minimal ATS scorer (runs entirely in the browser) ─────── */
const POWER_VERBS = ['led','built','developed','managed','designed','achieved','improved',
  'increased','reduced','launched','delivered','created','implemented','optimised',
  'streamlined','collaborated','presented','negotiated','generated','coordinated'];

const WEAK_PHRASES = ['responsible for','worked on','helped with','assisted in','involved in',
  'duties included','team player','hard worker','detail oriented'];

function scoreResume(resumeText, jobDesc) {
  const resume  = (resumeText  || '').toLowerCase();
  const job     = (jobDesc     || '').toLowerCase();
  const results = [];

  /* 1. Contact info */
  const hasEmail  = /@[a-z]+\.[a-z]+/.test(resume);
  const hasPhone  = /\d{10}|\+\d{1,3}[\s-]\d/.test(resume);
  const hasLinked = /linkedin/.test(resume);
  const contactScore = ((hasEmail ? 1 : 0) + (hasPhone ? 1 : 0) + (hasLinked ? 1 : 0)) / 3 * 100;
  results.push({
    label   : 'Contact & Profile',
    score   : Math.round(contactScore),
    max     : 100,
    tips    : [
      !hasEmail  && 'Add a professional email address',
      !hasPhone  && 'Include a phone number',
      !hasLinked && 'Add your LinkedIn URL',
    ].filter(Boolean),
  });

  /* 2. Keyword match (only if job desc provided) */
  if (job.trim()) {
    const jobWords = [...new Set(job.match(/\b[a-z]{4,}\b/g) || [])].filter(w => w.length > 4);
    const matched  = jobWords.filter(w => resume.includes(w));
    const pct      = jobWords.length ? Math.round((matched.length / jobWords.length) * 100) : 0;
    const missing  = jobWords.filter(w => !resume.includes(w)).slice(0, 5);
    results.push({
      label   : 'Keyword Match',
      score   : Math.min(pct, 100),
      max     : 100,
      tips    : missing.length ? [`Consider adding: ${missing.join(', ')}`] : ['Great keyword coverage!'],
    });
  }

  /* 3. Action verbs */
  const verbCount  = POWER_VERBS.filter(v => resume.includes(v)).length;
  const verbScore  = Math.min(Math.round((verbCount / 8) * 100), 100);
  const missingVerbs = POWER_VERBS.filter(v => !resume.includes(v)).slice(0, 3);
  results.push({
    label   : 'Action Verbs',
    score   : verbScore,
    max     : 100,
    tips    : verbScore < 80 ? [`Try verbs like: ${missingVerbs.join(', ')}`] : ['Strong action verbs used!'],
  });

  /* 4. Weak language */
  const weakFound  = WEAK_PHRASES.filter(p => resume.includes(p));
  const weakScore  = Math.max(0, 100 - weakFound.length * 20);
  results.push({
    label   : 'Language Quality',
    score   : weakScore,
    max     : 100,
    tips    : weakFound.length
      ? [`Avoid: "${weakFound[0]}" — replace with a strong action verb`]
      : ['Clean, direct language!'],
  });

  /* 5. Length & structure */
  const wordCount = (resumeText || '').split(/\s+/).filter(Boolean).length;
  const lengthScore = wordCount < 200 ? 40 : wordCount < 400 ? 70 : wordCount < 900 ? 100 : 75;
  results.push({
    label   : 'Length & Structure',
    score   : lengthScore,
    max     : 100,
    tips    : wordCount < 200
      ? ['Resume seems short — add more detail to key roles']
      : wordCount > 900
      ? ['Resume may be too long for ATS — aim for 1 page']
      : ['Good length!'],
  });

  const overall = Math.round(results.reduce((s, r) => s + r.score, 0) / results.length);
  return { overall, sections: results };
}

/* ── Colour helpers ───────────────────────────────────────── */
const scoreColor = (s) =>
  s >= 80 ? '#4A8C5C' : s >= 55 ? '#C8973A' : '#C0392B';

const scoreLabel = (s) =>
  s >= 80 ? 'Excellent' : s >= 55 ? 'Fair' : 'Needs Work';

/* ── Animated ring ────────────────────────────────────────── */
const ScoreRing = ({ score, size = 120 }) => {
  const r  = (size - 16) / 2;
  const cx = size / 2;
  const circumference = 2 * Math.PI * r;
  const dash = (score / 100) * circumference;
  const color = scoreColor(score);

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="rgba(26,24,20,0.08)" strokeWidth={12} />
      <circle
        cx={cx} cy={cx} r={r} fill="none"
        stroke={color} strokeWidth={12}
        strokeDasharray={`${dash} ${circumference}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)' }}
      />
    </svg>
  );
};

/* ── Bar row ─────────────────────────────────────────────── */
const ScoreBar = ({ label, score, tips }) => {
  const color = scoreColor(score);
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--rb-ink)' }}>{label}</span>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color }}>{score}%</span>
      </div>
      <div style={{ height: 7, borderRadius: 99, background: 'rgba(26,24,20,0.08)', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${score}%`, borderRadius: 99,
          background: color,
          transition: 'width 1s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>
      {tips.map((tip, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginTop: 5 }}>
          {score >= 80
            ? <CheckCircle size={11} style={{ color: '#4A8C5C', flexShrink: 0, marginTop: 1 }} />
            : <AlertCircle size={11} style={{ color, flexShrink: 0, marginTop: 1 }} />}
          <span style={{ fontSize: '0.72rem', color: 'var(--rb-muted)', lineHeight: 1.5 }}>{tip}</span>
        </div>
      ))}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const ATSScoreCard = ({ resumeText = '', onSkip, onContinue }) => {
  const [jobDesc,   setJobDesc]   = useState('');
  const [analysing, setAnalysing] = useState(false);
  const [result,    setResult]    = useState(null);
  const textareaRef = useRef(null);

  const runAnalysis = () => {
    setAnalysing(true);
    setResult(null);
    // Simulate a brief async check for UX polish
    setTimeout(() => {
      const data = scoreResume(resumeText, jobDesc);
      setResult(data);
      setAnalysing(false);
    }, 1400);
  };

  const handleContinue = () => onContinue(result);

  return (
    <div style={{
      background: 'var(--rb-card-bg)',
      border: '1px solid var(--rb-border)',
      borderRadius: 'var(--rb-radius)',
      boxShadow: 'var(--rb-shadow-lg)',
      overflow: 'hidden',
    }}>

      {/* Header */}
      <div style={{
        padding: '18px 24px 16px',
        borderBottom: '1px solid var(--rb-border)',
        background: 'var(--rb-gold-bg)',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'var(--rb-gold)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Zap size={17} color="#fff" />
        </div>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1rem', fontWeight: 700 }}>
            ATS Score Check
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--rb-muted)', marginTop: 2 }}>
            Free — takes under 5 seconds
          </div>
        </div>
        <button onClick={onSkip} style={{
          marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5,
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--rb-muted)', fontSize: '0.75rem', fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif", padding: '5px 0',
        }}>
          <SkipForward size={13} /> Skip
        </button>
      </div>

      <div style={{ padding: '22px 24px' }}>

        {!result && (
          <>
            {/* Info banner */}
            <div style={{
              display: 'flex', gap: 10, padding: '11px 14px', borderRadius: 10,
              background: 'rgba(200,151,58,0.07)', border: '1px solid rgba(200,151,58,0.18)',
              marginBottom: 20,
            }}>
              <Info size={14} style={{ color: 'var(--rb-gold)', flexShrink: 0, marginTop: 1 }} />
              <p style={{ margin: 0, fontSize: '0.77rem', color: 'var(--rb-muted)', lineHeight: 1.55 }}>
                Paste the job description below for a <strong style={{ color: 'var(--rb-ink)' }}>keyword match analysis</strong>.
                Leave it blank for a general ATS health check.
              </p>
            </div>

            {/* Job description textarea */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block', fontSize: '0.72rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.07em',
                color: 'var(--rb-muted)', marginBottom: 7,
              }}>
                Job Description (optional)
              </label>
              <textarea
                ref={textareaRef}
                value={jobDesc}
                onChange={e => setJobDesc(e.target.value)}
                placeholder="Paste the full job description here…"
                style={{
                  width: '100%', minHeight: 130, resize: 'vertical',
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.83rem',
                  color: 'var(--rb-ink)', background: 'var(--rb-cream)',
                  border: '1px solid var(--rb-border-md)', borderRadius: 10,
                  padding: '10px 13px', outline: 'none', lineHeight: 1.6,
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => { e.target.style.borderColor = 'var(--rb-gold)'; e.target.style.boxShadow = '0 0 0 3px rgba(200,151,58,0.12)'; }}
                onBlur={e  => { e.target.style.borderColor = 'var(--rb-border-md)'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={runAnalysis}
                disabled={analysing}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  padding: '11px 20px', borderRadius: 50, border: 'none', cursor: 'pointer',
                  background: 'var(--rb-ink)', color: 'var(--rb-cream)',
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.86rem', fontWeight: 700,
                  transition: 'all 0.18s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--rb-gold)'; e.currentTarget.style.color = 'var(--rb-ink)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--rb-ink)'; e.currentTarget.style.color = 'var(--rb-cream)'; }}
              >
                {analysing
                  ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Analysing…</>
                  : <><Zap size={14} /> Check My Score</>}
              </button>
            </div>

            {analysing && (
              <div style={{ textAlign: 'center', marginTop: 20, color: 'var(--rb-muted)', fontSize: '0.78rem' }}>
                Scanning your resume against ATS rules…
              </div>
            )}
          </>
        )}

        {/* ── Results ── */}
        {result && (
          <>
            {/* Overall ring */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 28 }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <ScoreRing score={result.overall} size={110} />
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: '1.6rem', fontWeight: 800, color: scoreColor(result.overall), lineHeight: 1 }}>
                    {result.overall}
                  </span>
                  <span style={{ fontSize: '0.62rem', fontWeight: 600, color: 'var(--rb-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    / 100
                  </span>
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.25rem', fontWeight: 700 }}>
                  {scoreLabel(result.overall)}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--rb-muted)', marginTop: 4, lineHeight: 1.5 }}>
                  {result.overall >= 80
                    ? 'Your resume is well-optimised for ATS systems.'
                    : result.overall >= 55
                    ? 'A few tweaks will significantly boost your chances.'
                    : 'Your resume needs attention before applying.'}
                </div>
                <div style={{
                  display: 'inline-block', marginTop: 8, padding: '3px 10px', borderRadius: 50,
                  background: 'rgba(200,151,58,0.10)', border: '1px solid rgba(200,151,58,0.22)',
                  fontSize: '0.7rem', fontWeight: 600, color: '#9A6F1E',
                }}>
                  Free check complete ✓
                </div>
              </div>
            </div>

            {/* Section bars */}
            <div style={{ borderTop: '1px solid var(--rb-border)', paddingTop: 20, marginBottom: 22 }}>
              {result.sections.map(sec => (
                <ScoreBar key={sec.label} label={sec.label} score={sec.score} tips={sec.tips} />
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={handleContinue}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '13px 20px', borderRadius: 50, border: 'none', cursor: 'pointer',
                background: 'var(--rb-ink)', color: 'var(--rb-cream)',
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 700,
                transition: 'all 0.18s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--rb-gold)'; e.currentTarget.style.color = 'var(--rb-ink)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--rb-ink)'; e.currentTarget.style.color = 'var(--rb-cream)'; }}
            >
              Continue to Download <ChevronRight size={16} />
            </button>
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ATSScoreCard;